import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Logo from '../components/Logo';
import { Spinner } from '../components/Spinner';
import { API_BASE } from '../server/api';

type Phase = 'loading' | 'success' | 'error';

type ErrorKind = 'link_used' | 'expired' | 'not_pending' | 'generic';

function IconCheckLarge() {
  return (
    <svg
      className="mx-auto h-16 w-16 text-accent"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path
        d="M18 33l10 10 18-22"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconXLarge() {
  return (
    <svg
      className="mx-auto h-16 w-16 text-danger"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path
        d="M24 24l16 16M40 24L24 40"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function parseErrorKind(status: number, message: string): ErrorKind {
  const m = message.toLowerCase();
  if (status === 404) return 'link_used';
  if (status === 410) {
    if (m.includes('expired')) return 'expired';
    return 'link_used';
  }
  if (status === 400 && m.includes('not scheduled for deletion')) return 'not_pending';
  return 'generic';
}

function errorCopy(kind: ErrorKind): { title: string; body: string } {
  switch (kind) {
    case 'link_used':
      return {
        title: 'Link Already Used',
        body: 'This approval link has already been used or is invalid.',
      };
    case 'expired':
      return {
        title: 'Link Expired',
        body: 'This approval link has expired. Please contact hello@lognsec.com to cancel your deletion manually.',
      };
    case 'not_pending':
      return {
        title: 'No Action Needed',
        body: 'This account is not currently scheduled for deletion.',
      };
    default:
      return {
        title: 'Something Went Wrong',
        body: 'Please contact hello@lognsec.com for assistance.',
      };
  }
}

export default function ApproveCancelDeletionPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';

  const [phase, setPhase] = useState<Phase>(() => (token ? 'loading' : 'error'));
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<ErrorKind>('generic');

  useEffect(() => {
    if (!token) {
      setPhase('error');
      setErrorKind('generic');
      return;
    }

    let cancelled = false;
    setPhase('loading');
    setBusinessName(null);

    void (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/account/approve-cancel-deletion?token=${encodeURIComponent(token)}`,
          { method: 'GET', headers: { Accept: 'application/json' } }
        );
        const data = (await res.json().catch(() => ({}))) as {
          success?: boolean;
          businessName?: string;
          error?: string;
        };

        if (cancelled) return;

        if (res.ok && data.success && typeof data.businessName === 'string') {
          setBusinessName(data.businessName);
          setPhase('success');
          return;
        }

        const msg = typeof data.error === 'string' ? data.error : '';
        setErrorKind(parseErrorKind(res.status, msg));
        setPhase('error');
      } catch {
        if (!cancelled) {
          setErrorKind('generic');
          setPhase('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const contactLink = (
    <a href="mailto:hello@lognsec.com" className="text-sm font-medium text-accent hover:underline">
      Questions? Contact hello@lognsec.com
    </a>
  );

  const errorContact = (
    <p className="mt-6 text-center">
      <a href="mailto:hello@lognsec.com" className="text-sm font-medium text-accent hover:underline">
        hello@lognsec.com
      </a>
    </p>
  );

  return (
    <main className="flex min-h-screen items-center justify-center bg-navy px-4 py-10 text-foreground sm:px-6 sm:py-14">
      <div className="w-full max-w-[480px] rounded-2xl border border-border bg-surface p-10 shadow-xl">
        <div className="mb-8 flex justify-center">
          <Logo size="lg" />
        </div>

        {!token ? (
          <>
            <IconXLarge />
            <h1 className="mt-6 text-center text-xl font-bold tracking-tight">
              {errorCopy('generic').title}
            </h1>
            <p className="mt-3 text-center text-sm text-muted leading-relaxed">
              {errorCopy('generic').body}
            </p>
            {errorContact}
          </>
        ) : phase === 'loading' ? (
          <div className="flex flex-col items-center text-center">
            <Spinner className="h-10 w-10 text-accent" />
            <p className="mt-6 text-sm font-medium text-muted">Verifying your approval...</p>
          </div>
        ) : phase === 'success' && businessName ? (
          <>
            <IconCheckLarge />
            <h1 className="mt-6 text-center text-2xl font-bold tracking-tight">
              Account Deletion Cancelled
            </h1>
            <p className="mt-3 text-center text-sm text-foreground leading-relaxed">
              Your logNsec account for <strong>{businessName}</strong> is fully active again.
            </p>
            <p className="mt-3 text-center text-sm text-muted leading-relaxed">
              You have full access to your account. We&apos;re glad you&apos;re staying.
            </p>
            <Link
              to="/login"
              className="mt-8 flex w-full min-h-[48px] items-center justify-center rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-cta-text transition hover:bg-accent/90"
            >
              Log In to My Dashboard
            </Link>
            <p className="mt-6 text-center">{contactLink}</p>
          </>
        ) : (
          <>
            <IconXLarge />
            <h1 className="mt-6 text-center text-xl font-bold tracking-tight">
              {errorCopy(errorKind).title}
            </h1>
            <p className="mt-3 text-center text-sm text-muted leading-relaxed">
              {errorCopy(errorKind).body}
            </p>
            {errorContact}
          </>
        )}
      </div>
    </main>
  );
}
